<template>
    <section>
        <e-sidebar
            v-model="open"
            type="is-white"
            can-cancel
            fullheight
            right
            :fullwidth="$isMobile"
            :on-cancel="closeAlertDrawer"
            :class="['alert-drawer', $isMobile ? 'is-mobile' : '']"
        >
            <aside>
                <div class="is-flex-direction-column card-header">
                    <div class="level mb-0" :style="$isMobile ? 'display:flex;margin-bottom:0' : ''">
                        <div class="level-left">
                            <div class="level-item">
                                <h2 class="card-header-title title is-5 is-flex is-align-center">
                                    Notifications
                                    <div
                                        v-if="!isLoaded && notifications.length"
                                        class="notifications-counter is-relative is-flex is-align-center is-justify-content-center ml-3"
                                    >
                                        {{ notificationsCounter }}
                                    </div>
                                </h2>
                            </div>
                        </div>

                        <div class="level-right card-header-title">
                            <e-tooltip label="Mark All as Read" append-to-body position="is-left">
                                <e-button
                                    class="mr-2"
                                    icon-right="check-circle"
                                    aria-label="Mark All as Read"
                                    :disabled="!notifications.length"
                                    @click="markAllAsRead"
                                />
                            </e-tooltip>
                            <e-tooltip label="Close" append-to-body position="is-left">
                                <e-button
                                    icon-right="times"
                                    aria-label="Close"
                                    @click="closeAlertDrawer()"
                                />
                            </e-tooltip>
                        </div>
                    </div>
                </div>
                <div v-if="!isLoaded">
                    <alert-card
                        v-for="notification in notifications"
                        :key="notification.notification_id"
                        :notification="notification"
                        @update-notification="updateNotification"
                    />
                    <infinite-loading
                        v-if="hasNextPage && !infiniteIsLoading"
                        spinner="spiral"
                        @distance="4"
                        @infinite="handleLoadMore"
                    />
                </div>
                <div v-if="notifications.length === 0">
                    <p class="has-text-centered has-text-grey-light mt-5">No notifications to display.</p>
                </div>
            </aside>
        </e-sidebar>
    </section>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';
import { mapState } from 'vuex';

import AlertCard from '@/components/alerts/AlertCard.vue';
import BellNotification from '@/models/BellNotification.js';

export default {
    name: 'AlertDrawer',

    components: { AlertCard, InfiniteLoading },
    emits: ['close-alert-drawer'],

    data: () => ({
        isLoaded: true,
        infiniteIsLoading: false,
        updatedNotifications: [],
    }),

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        open() {
            return this.$store.state.alertDrawer;
        },

        notificationsCounter() {
            return this.$store.state.entities.bell_notifications.counter;
        },

        notifications() {
            return BellNotification.query().orderBy('created_at', 'desc').get();
        },

        hasNextPage() {
            return (
                this.$store.state.entities.bell_notifications.page <
                this.$store.state.entities.bell_notifications.last_page
            );
        },

        currentPage: {
            get() {
                return this.$store.state.entities.bell_notifications.page;
            },

            set(value) {
                BellNotification.commit(state => (state.page = value));
            },
        },
    },

    async mounted() {
        this.isLoaded = false;
    },

    unmounted() {
        this.currentPage = 1;
    },

    methods: {
        updateNotification({ notification_id }) {
            this.updatedNotifications = this.updatedNotifications.includes(notification_id)
                ? this.updatedNotifications.filter(id => id !== notification_id)
                : [...this.updatedNotifications, notification_id];
        },

        async closeAlertDrawer() {
            this.$emit('close-alert-drawer');
            this.$store.commit('SET_ALERT_DRAWER', { state: false });

            this.infiniteIsLoading = false;
            this.hasNextPage = true;

            if (this.updatedNotifications.length) {
                this.currentPage = 1;
                const notificationModels = BellNotification.query().findIn(this.updatedNotifications);
                await Promise.all(notificationModels.map(notification => notification.markAsRead()));
                notificationModels.forEach(notification => BellNotification.delete(notification.notification_id));
                await BellNotification.fetch(1);
                this.updatedNotifications = [];

                // Reload page 1 from the server now that the reads are committed.
                // fetch() sets `counter` from meta.total of the same query that
                // populates the drawer, so the badge is authoritative whether one,
                // many, or all notifications were marked read.
                await BellNotification.fetch();
            }
        },

        async fetchNotifications() {
            return BellNotification.api().get(`/api/notifications?page=${this.currentPage}`);
        },

        async handleLoadMore() {
            if (this.hasNextPage) {
                this.infiniteIsLoading = true;
                this.currentPage += 1;
                await BellNotification.fetch(this.currentPage);
                this.infiniteIsLoading = false;
            }
        },

        async markAllAsRead() {
            const ids = this.notifications.map(notification => notification.notification_id);
            await BellNotification.update({
                where: notification => {
                    return ids.includes(notification.notification_id);
                },
                data: { read: true },
            });

            this.updatedNotifications = ids;
        },
    },
};
</script>
<style lang="scss">
@import '~assets/scss/toolbox';
.alert-drawer.p-drawer:not(.is-mobile) {
    width: 480px;
}
.alert-drawer .card-header {
    border-bottom: 1px solid $grey-lighter;
}
.notifications-counter {
    color: white;
    width: 22px;
    height: 22px;
    line-height: 0;
    font-size: 12px;
    border-radius: 50%;
    background-color: #ff5e73;
}
</style>
